import {
  ButtonV3,
  CheckmarkListItem,
  List,
  Price,
  RingFinishEnum,
  TypographyV3,
} from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import AffirmPromo, {
  getAffirmPrice,
} from '@/app/[locale]/product/_components/AffirmPromo';
import useShowPDPAffirmMessage from '@/app/[locale]/product/_hooks/useShowPDPAffirmMessage';
import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';
import useCurrency from '@/app/hooks/useCurrency';
import type { ListProduct } from '@/queries/Products';

import type { CurrentRouteValues } from '../_hooks/useCurrentRoute';
import type { Order } from '../_types/order';
import { getPriceAndDiscount } from '../_utils/getPriceAndDiscount';

const OR_4_RING_IMAGES = {
  [RingFinishEnum.Black]: 'hardware-pdp/hero-carousel/or4-black-angle-view.png',
  [RingFinishEnum.BrushedSilver]:
    'hardware-pdp/hero-carousel/or4-brushed-silver-angle-view.png',
  [RingFinishEnum.Gold]: 'hardware-pdp/hero-carousel/or4-gold-angle-view.png',
  [RingFinishEnum.RoseGold]:
    'hardware-pdp/hero-carousel/or4-rose-gold-angle-view.png',
  [RingFinishEnum.Silver]:
    'hardware-pdp/hero-carousel/or4-silver-angle-view.png',
  [RingFinishEnum.Stealth]:
    'hardware-pdp/hero-carousel/or4-stealth-angle-view.png',
};

export interface OrderSummaryBaseProps {
  readonly currentRoute: CurrentRouteValues;
  readonly isAddingToCart: boolean;
  readonly onAddToCart: () => Promise<void>;
  readonly product: ListProduct | undefined;
  readonly productName: MessageKey;
  readonly subtitle: MessageKey;
  readonly summaryPrice: {
    totalDiscount: number;
    totalPrice: number;
  };
  readonly tag: ReactNode;
  readonly title: MessageKey;
}

interface OrderSummaryProps extends OrderSummaryBaseProps {
  readonly monthlyMembership: ListProduct | undefined;
  readonly order: Order;
}

const priceTValues = ({
  currencyCode,
  amount,
}: {
  currencyCode: string;
  amount: string | number;
}) => ({
  price: function price() {
    return <Price currencyCode={currencyCode} price={amount} />;
  },
});

const sizeTValues = ({ size }: { size: string }) => ({
  amount: function amount() {
    return <span className="text-sandstone-500">&nbsp;{size}</span>;
  },
});

const OrderSummary = ({
  currentRoute,
  isAddingToCart,
  onAddToCart,
  monthlyMembership,
  product,
  productName,
  subtitle,
  summaryPrice,
  tag,
  title,
  order,
}: OrderSummaryProps) => {
  const t = useTranslations();
  const { currencyCode } = useCurrency();
  const { discountPrice, price } = getPriceAndDiscount(product);
  const discountedProductPrice = product?.discountedPrice;

  const showAffirm = useShowPDPAffirmMessage(
    discountedProductPrice,
    product?.handle,
  );
  const { price: membershipMonthlyPrice } =
    getPriceAndDiscount(monthlyMembership);
  const affirmPrice = getAffirmPrice(discountPrice, price!);
  const imageSrc =
    OR_4_RING_IMAGES[
      currentRoute.finish.value as keyof typeof OR_4_RING_IMAGES
    ];

  if (!product) {
    return null;
  }

  return (
    <div className="flex flex-col items-center md:items-stretch">
      <div className="w-full max-w-sm">
        <TypographyV3
          Element="h3"
          className="text-3xl"
          height="tight"
          variant="unset"
        >
          {t(title)}
        </TypographyV3>
        <TypographyV3
          Element="h4"
          className="text-3xl"
          font="serif-italic"
          height="tight"
          variant="unset"
        >
          {t(subtitle)}
        </TypographyV3>
        <Image
          alt={product.lineItemImage?.alt ?? ''}
          className="mx-auto my-12"
          format="png"
          height={215}
          src={imageSrc}
          width={215}
        />
        <div className="mb-4 rounded-xl bg-sandstone-300 p-6">
          {tag}
          <TypographyV3 className="text-3xl tracking-tighter">
            {t(productName)}
          </TypographyV3>
          <TypographyV3>{t(product.colorMsgKey!)}</TypographyV3>
          <List className="my-6 list-inside" unstyled>
            <CheckmarkListItem>
              <RichText
                messageKey={order.size.summaryKey!}
                tValues={sizeTValues({
                  size: order.size.value,
                })}
              />
            </CheckmarkListItem>
            <CheckmarkListItem>
              <RichText
                messageKey={order.membership.summaryKey!}
                tValues={priceTValues({
                  amount: membershipMonthlyPrice!,
                  currencyCode,
                })}
              />
            </CheckmarkListItem>
            <CheckmarkListItem>
              {t(order.warranty.summaryKey!)}
            </CheckmarkListItem>
          </List>
          <Price
            className="text-2xl super:text-3xl"
            currencyCode={currencyCode}
            currencyCodeStyles="text-base"
            discount={summaryPrice.totalDiscount}
            price={summaryPrice.totalPrice}
            showCurrency
          />
          {showAffirm && (
            <TypographyV3 className="text-xs" Element="div">
              <AffirmPromo
                className="text-sandstone-500"
                pageType="product"
                price={affirmPrice}
              />
            </TypographyV3>
          )}
          <ButtonV3
            className="mt-6 w-full"
            data-cy="add-to-cart-button"
            disabled={isAddingToCart}
            loading={isAddingToCart}
            loadingTextEnabled
            onClick={onAddToCart}
            size="large"
            variant="primary"
          >
            {t('pdp_add_to_cart')}
          </ButtonV3>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
